var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from "lit";
import { property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { styles } from "./style.js";
// Module-level caches shared across all <w-icon> instances on the page.
// _svgCache: resolved SVGElements (null = failed fetch, kept to avoid retries).
// _fetchMap: in-flight promises, so N simultaneous icons with the same name
//            issue exactly one HTTP request and all await the same promise.
const _svgCache = new Map();
const _fetchMap = new Map();
function buildIconUrl(name, locale) {
    return `https://assets.finn.no/pkg/eikons/~1/${locale}/${name}.svg`;
}
function parseSvg(text) {
    const doc = new DOMParser().parseFromString(text, "image/svg+xml");
    if (doc.querySelector("parsererror"))
        return null;
    return doc.querySelector("svg");
}
function fetchIcon(url) {
    if (_svgCache.has(url))
        return Promise.resolve(_svgCache.get(url) ?? null);
    const pending = _fetchMap.get(url);
    if (pending !== undefined)
        return pending;
    const promise = fetch(url)
        .then((res) => {
        if (!res.ok)
            throw new Error(`HTTP ${res.status}`);
        return res.text();
    })
        .then((text) => {
        const svg = parseSvg(text);
        _svgCache.set(url, svg);
        return svg;
    })
        .catch(() => {
        _svgCache.set(url, null);
        return null;
    })
        .finally(() => {
        _fetchMap.delete(url);
    });
    _fetchMap.set(url, promise);
    return promise;
}
/**
 * [Warp component reference](https://warp-ds.github.io/docs/components/icons/frameworks/elements)
 */
export class WarpIcon extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Locale used for `<title>` text.
         *
         * Reads the `lang` attribute from `<html>`, falls back to 'en'.
         */
        this.locale = document.documentElement.lang || "en";
        this.svg = null;
        this._ready = false;
        // Minted fresh on each loadIcon() call. If name/locale changes while a
        // fetch is in flight the stale response sees a mismatched token and is
        // discarded, preventing it from overwriting the current icon.
        this._loadToken = null;
    }
    static { this.styles = [styles]; }
    updated(changedProps) {
        if (changedProps.has("name") || changedProps.has("locale")) {
            void this.loadIcon();
        }
    }
    async loadIcon() {
        if (!this.name) {
            this.svg = null;
            this._ready = false;
            return;
        }
        const url = buildIconUrl(this.name, this.locale);
        const token = Symbol();
        this._loadToken = token;
        this._ready = false;
        const svg = await fetchIcon(url);
        if (this._loadToken !== token)
            return;
        if (svg) {
            this.svg = svg.cloneNode(true);
            this._ready = true;
        }
        else {
            this.svg = null;
        }
    }
    render() {
        const size = this.size || "medium";
        const name = this.name || "";
        const classes = {
            "w-icon": true,
            "w-icon--ready": this._ready,
            "w-icon--s": size === "small",
            "w-icon--m": size === "medium",
            "w-icon--l": size === "large",
        };
        const customStyle = typeof size === "string" && size.endsWith("px")
            ? `--w-icon-size: ${size};`
            : "";
        return html `<div
			class="${classMap(classes)}"
			style="${customStyle}"
			part="w-${name.toLowerCase()}"
		>
			${this.svg}
		</div>`;
    }
}
__decorate([
    property({ type: String, reflect: true })
], WarpIcon.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpIcon.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true, useDefault: true })
], WarpIcon.prototype, "locale", void 0);
__decorate([
    state()
], WarpIcon.prototype, "svg", void 0);
__decorate([
    state()
], WarpIcon.prototype, "_ready", void 0);
/** @deprecated Exported for backwards compatibility. Use WarpIcon. */
export const WIcon = WarpIcon;
if (!customElements.get("w-icon")) {
    customElements.define("w-icon", WarpIcon);
}
//# sourceMappingURL=icon.js.map